import { defineStore } from "pinia";
import { DatasetLocation } from "@/types/location/DatasetLocation";
import { BuildingCountResult, IAccumulationsSettings } from "@/types";
import { groupBy, map, omit } from "lodash-es";
import {
  useGammaData,
  useGammaWebServices,
  useMap,
  useOrgSettings,
} from "@/services";
import { Geometry } from "geojson";

export interface IAccumulationRecord {
  businessLine: string;
  value: number;
  count: number;
}

export interface IAccumulationType {
  type: string;
  accumulations: IAccumulationRecord[];
  totalCount: number;
  totalValue: number;
}

export interface IAccumulation {
  buffer: number;
  default?: boolean;
  accumulationTypes: IAccumulationType[];
}

export interface AccumulationsState {
  accumulations: IAccumulation[];
  isLoadingAccumulations: boolean;
}

export const useAccumulations = defineStore("accumulations", {
  state: (): AccumulationsState => ({
    accumulations: [],
    isLoadingAccumulations: false,
  }),
  actions: {
    async fetchLocationAccumulations(
      location: DatasetLocation,
      accumulationSettings: IAccumulationsSettings | null
    ): Promise<IAccumulation[]> {
      this.accumulations = [];
      this.isLoadingAccumulations = true;
      this.accumulations = accumulationSettings
        ? await Promise.all(
            accumulationSettings.buffers.map(async (buffer) => ({
              accumulationTypes: await this.fetchAccumulationsData(
                accumulationSettings,
                location,
                buffer
              ),
              buffer,
              default: accumulationSettings.defaultBuffer === buffer,
            }))
          )
        : [];
      this.isLoadingAccumulations = false;
      return this.accumulations;
    },
    async fetchAccumulationsData(
      accumulationSettings: IAccumulationsSettings,
      location: DatasetLocation,
      buffer: number | undefined
    ): Promise<IAccumulationType[]> {
      const datasetTypes = [
        ...new Set(
          accumulationSettings.accumulationsLookups.map((o) => o.dataSetType)
        ),
      ];
      const businessLines = [
        ...new Set(
          accumulationSettings.accumulationsLookups.map((o) => o.businessLine)
        ),
      ];
      return Promise.all(
        datasetTypes
          .map((datasetType) =>
            businessLines.map((businessLine) =>
              useGammaData().fetchAccumulationsRecord({
                lcId: `${businessLine}_${datasetType}`.toLowerCase(),
                coordinates: location.geometry.coordinates as number[],
                bufferValue: buffer ?? accumulationSettings.defaultBuffer,
              })
            )
          )
          .flat()
      ).then((r) =>
        map(groupBy(r, "datasetType"), (records, type: string) => ({
          type,
          accumulations: records.map((o: any) =>
            omit(o, "datasetType")
          ) as IAccumulationRecord[],
          totalCount: map(records, "count").reduce(
            (a: number, b: number) => a + b,
            0
          ),
          totalValue: map(records, "value").reduce(
            (a: number, b: number) => a + b,
            0
          ),
        }))
      );
    },
    convertPolygonToWKT(geometry: Geometry): { WKTGeometry: string } {
      if (geometry.type !== "Polygon") {
        throw new Error("Only Polygon type is supported");
      }

      const coordinatesString = geometry.coordinates[0]
        .map((coord) => `${coord[0]} ${coord[1]}`)
        .join(",");

      return {
        WKTGeometry: `POLYGON((${coordinatesString}))`,
      };
    },
    async loadBuildingCountData(): Promise<BuildingCountResult | undefined> {
      const buildingCount = useOrgSettings().accumulations?.buildingCountLookup;
      const geometry = useMap().dataBoundary?.geometry;
      if (!buildingCount) return;
      if (!geometry) return;
      const wktGeometry = this.convertPolygonToWKT(geometry);
      try {
        return await useGammaWebServices().fetchBuildingCountData(
          wktGeometry,
          buildingCount?.url,
          buildingCount?.apiKey
        );
      } catch (e) {
        console.error(e);
        return;
      }
    },
  },
});
